import * as types from './actionTypes';
import artistAdminService from '../services/artistAdminService';
import { fetch, addTask } from 'domain-task';
import ToastrService from '../services/toastrService';
import * as accountActions from './accountActions';

export function createProfile(data){
    return function(dispatch) {
        dispatch({type: types.CLEAR_CREATE_PROFILE_ERROR});
        return artistAdminService.createProfile(data).then(profile => {            
            dispatch({ type: types.CREATE_PROFILE_SUCCESS, profile });
            const toastrService = new ToastrService();
            toastrService.showSuccess('', 'Your profile has been created successfully');
            return profile;
        }).catch(error => {
            dispatch({type: types.SET_CREATE_PROFILE_ERROR, error});
            return null;
        });
    };
}

export function updateProfile(id, data, showToast = true){
    return function(dispatch) {
        dispatch({type: types.CLEAR_CREATE_PROFILE_ERROR});
        return artistAdminService.updateProfile(id, data).then(profile => {            
            dispatch({ type: types.UPDATE_PROFILE_SUCCESS, profile });
            if(showToast) {
                const toastrService = new ToastrService();
                toastrService.showSuccess('', 'Your profile has been updated successfully');
            }
            return profile;
        }).catch(error => {
            dispatch({type: types.SET_CREATE_PROFILE_ERROR, error});
            return null;
        });
    };
}

export function enableDisableProfile(id, enable){
    return function(dispatch, getState) {
        dispatch({type: types.CLEAR_CREATE_PROFILE_ERROR});
        const toastrService = new ToastrService();
        return artistAdminService.enableDisableProfile(id, enable).then(profile => {            
            dispatch({ type: types.UPDATE_PROFILE_SUCCESS, profile });
            toastrService.showSuccess('', `Your profile has been ${enable ? 'enabled' : 'disabled'} successfully`);
            if(!enable) {
                //if it was the active profile, change it
                const state = getState();
                const user = state.account.user;
                if(user.activeProfile && !user.activeProfile.Enabled && user.enabledProfiles.length) {
                    dispatch(accountActions.updateActiveProfile(user.enabledProfiles[0].ID));
                }
            }
            return profile;
        }).catch(error => {
            toastrService.showError('', error.message);
            return null;
        });
    };
}

export function clearCreateProfileError() {
    return function(dispatch) {
        dispatch({ type: types.CLEAR_CREATE_PROFILE_ERROR });
    };  
}

export function updateProfileImages(id, images) {
    return (dispatch, getState) => {
        dispatch({ type: types.PROFILE_IMAGES_UPDATED, profile: { id, images }});
        dispatch({ type: types.CLEAR_PROFILE_BROWSE_RESULTS });
    };
}

export function clearProfileTracks(id) {
    return (dispatch, getState) => {
        dispatch({ type: types.CLEAR_PROFILE_TRACKS, profile: { id }});
        dispatch({ type: types.CLEAR_PROFILE_BROWSE_RESULTS });
    };
}

export function clearProfileVideos(id) {
    return (dispatch, getState) => {
        dispatch({ type: types.CLEAR_PROFILE_VIDEOS, profile: { id }});
        dispatch({ type: types.CLEAR_PROFILE_BROWSE_RESULTS });
    };
}

export function clearProfileGigs(id, username) {
    return (dispatch, getState) => {
        dispatch({ type: types.CLEAR_PROFILE_GIGS, profile: { id, username }});
        dispatch({ type: types.CLEAR_GIG_BROWSE_RESULTS });
    };
}

export function clearProfileBuyOptions(id) {
    return (dispatch, getState) => {
        dispatch({ type: types.CLEAR_PROFILE_BUY_OPTIONS, profile: { id }});
    };
}

export function clearProfileAddress(id) {
    return (dispatch, getState) => {
        dispatch({ type: types.CLEAR_PROFILE_ADDRESS, profile: { id }});
    };
}

export function saveSoundCloudToken(soundCloudToken) {
    return { type: types.SOUNDCLOUD_LOGIN_SUCCESS, soundCloudToken };
}

export function wrapServiceCall(func) {
    //we just want to make a service call without dispatching anything - so we can go through middleware and check the token
    return function(dispatch) {
        return func()
            .then(data => data);    
    }
}

export function getMessages(profileID, sentReceived) {
    return wrapServiceCall(() => artistAdminService.getMessages(profileID, sentReceived));
}

export function getConversation(profileID, conversationID) {
    return wrapServiceCall(() => artistAdminService.getConversation(profileID, conversationID));
}

export function deleteConversation(profileID, conversationID) {
    return wrapServiceCall(() => artistAdminService.deleteConversation(profileID, conversationID));
}

export function getNotifications(profileID, page) {
    return wrapServiceCall(() => artistAdminService.getNotifications(profileID, page));
}

export function getBuyOptions(profileID) {
    return wrapServiceCall(() => artistAdminService.getBuyOptions(profileID));
}

export function deleteBuyOption(profileID, buyOptionID) {
    return wrapServiceCall(() => artistAdminService.deleteBuyOption(profileID, buyOptionID));
}

export function addBuyOption(profileID, buyOption) {
    return wrapServiceCall(() => artistAdminService.addBuyOption(profileID, buyOption));
}

export function getChatSettings(profileID) {
    return wrapServiceCall(() => artistAdminService.getChatSettings(profileID));
}

export function updateChatSettings(profileID, chatSettings) {
    return wrapServiceCall(() => artistAdminService.updateChatSettings(profileID, chatSettings));
}

export function getAddress(profileID) {
    return wrapServiceCall(() => artistAdminService.getAddress(profileID));
}

export function updateAddress(profileID, address) {
    return wrapServiceCall(() => artistAdminService.updateAddress(profileID, address));
}

export function getBio(profileID) {
    return wrapServiceCall(() => artistAdminService.getBio(profileID));
}

export function getAssociations(profileID) {
    return wrapServiceCall(() => artistAdminService.getAssociations(profileID));
}

export function deleteAssociation(profileID, associationID) {
    return wrapServiceCall(() => artistAdminService.deleteAssociation(profileID, associationID));
}

export function addAssociation(profileID, association) {
    return wrapServiceCall(() => artistAdminService.addAssociation(profileID, association));
}

export function createBio(profileID, bio) {
    return wrapServiceCall(() => artistAdminService.createBio(profileID, bio));
}

export function addSoundCloudTrack(profileID, trackID) {
    return wrapServiceCall(() => artistAdminService.addSoundCloudTrack(profileID, trackID));
}

export function getTracks(profileID) {
    return wrapServiceCall(() => artistAdminService.getTracks(profileID));
}

export function deleteTrack(profileID, trackID) {
    return wrapServiceCall(() => artistAdminService.deleteTrack(profileID, trackID));
}

export function uploadTrack(profileID, soundCloudToken, newTrack) {
    return wrapServiceCall(() => artistAdminService.uploadTrack(profileID, soundCloudToken, newTrack));
}

export function updateTrack(profileID, trackID, trackName, trackDescription, trackFeatured) {
    return wrapServiceCall(() => artistAdminService.updateTrack(profileID, trackID, trackName, trackDescription, trackFeatured));
}

export function getSoundCloudTracks(soundCloudToken) {
    return wrapServiceCall(() => artistAdminService.getSoundCloudTracks(soundCloudToken));
}

export function getImages(profileID) {
    return wrapServiceCall(() => artistAdminService.getImages(profileID));
}

export function deleteImage(profileID, imageID) {
    return wrapServiceCall(() => artistAdminService.deleteImage(profileID, imageID));
}

export function uploadImage(profileID, file) {
    return wrapServiceCall(() => artistAdminService.uploadImage(profileID, file));
}

export function updateImage(profileID, imageID, imageName, imageDescription, imageFeatured, imageData) {
    return wrapServiceCall(() => artistAdminService.updateImage(profileID, imageID, imageName, imageDescription, imageFeatured, imageData));
}

export function getGigs(profileID) {
    return wrapServiceCall(() => artistAdminService.getGigs(profileID));
}

export function deleteGig(profileID, gigID) {
    return wrapServiceCall(() => artistAdminService.deleteGig(profileID, gigID));
}

export function addGig(profileID, gig) {
    return wrapServiceCall(() => artistAdminService.addGig(profileID, gig));
}

export function updateGig(profileID, gigID, gig) {
    return wrapServiceCall(() => artistAdminService.updateGig(profileID, gigID, gig));
}

export function addVideo(profileID, video) {
    return wrapServiceCall(() => artistAdminService.addVideo(profileID, video));
}

export function updateVideo(profileID, videoID, video) {
    return wrapServiceCall(() => artistAdminService.updateVideo(profileID, videoID, video));
}

export function getVideos(profileID) {
    return wrapServiceCall(() => artistAdminService.getVideos(profileID));
}

export function deleteVideo(profileID, videoID) {
    return wrapServiceCall(() => artistAdminService.deleteVideo(profileID, videoID));
}
